import { useEffect, useMemo, useState } from 'react';

import {
  updateIndirectTaxFilingV2Contributions,
  useIndirectTaxFilingV2Contributions,
} from '@commenda/api/indirect-tax/filings';
import {
  IIndirectTaxFilingV2Contribution,
  IndirectTaxFilingV2ContributionBoxType,
} from '@commenda/api/indirect-tax/filings/types';
import {
  updateServiceRequestIssue,
  updateTask,
  useServiceRequestIssues,
} from '@commenda/api/service-request';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Skeleton,
} from '@commenda/kakashi';
import { ServiceRequestIssueStatus, TaskStatus } from '@prisma/client';

import { useNotification } from '../../../../hooks/useNotification';
import FilingInputRow from '../../../../sales-tax/filing/filing-review/FilingInputRow';
import { IndirectTaxRegionHelper } from '../../../../indirect-tax/common';
import {
  IndirectTaxFilingReturnSummaryHelper,
  IndirectTaxFilingsHelper,
} from '../../../../indirect-tax/filings/helpers';

interface IndirectTaxFilingVatReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  filingId: string;
  corporationId: number;
  // Numeric service-request task id for GENERATE_FILING_REPORT — used to
  // complete the task on Submit.
  taskId: number;
  serviceRequestId: string;
  currency: string;
  // Refetches the service request + filing GET so the activity timeline and
  // the (now-visible) return summary reflect the submitted report.
  onSubmitted: () => void;
}

interface VatReportSection {
  title: string;
  rows: IIndirectTaxFilingV2Contribution[];
}

const isEditable = (contribution: IIndirectTaxFilingV2Contribution): boolean =>
  contribution.boxType === IndirectTaxFilingV2ContributionBoxType.AGGREGATE;

// Box label mirrors the return summary: name + applied-rate suffix when the
// box resolves to a concrete percentage (disambiguates OSS reduced-rate rows).
const boxLabel = (contribution: IIndirectTaxFilingV2Contribution): string => {
  const ratePercent = IndirectTaxFilingReturnSummaryHelper.formatAppliedRate(
    contribution.appliedRate
  );
  return ratePercent
    ? `${contribution.boxName} (${ratePercent})`
    : contribution.boxName;
};

// MAJOR-unit display string for a contribution (value/adjustedValue are MINOR
// units). Used as the seed for editable rows and as the live read-only value
// for FORMULA rows.
const contributionMajorString = (
  contribution: IIndirectTaxFilingV2Contribution
): string => String((contribution.adjustedValue ?? contribution.value) / 100);

export const IndirectTaxFilingVatReportModal = ({
  isOpen,
  onClose,
  filingId,
  corporationId,
  taskId,
  serviceRequestId,
  currency,
  onSubmitted,
}: IndirectTaxFilingVatReportModalProps) => {
  const { setNotification } = useNotification();

  const {
    contributions,
    isLoading,
    mutate: mutateContributions,
  } = useIndirectTaxFilingV2Contributions(
    isOpen ? filingId : null,
    isOpen ? { corporationId } : null
  );

  // Active issues on this filing's service request (raised by the client via
  // "Report issue"). Resolved on Submit so a re-submitted report clears the
  // open issue.
  const { data: issuesData, mutate: mutateIssues } = useServiceRequestIssues({
    serviceRequestId,
    status: ServiceRequestIssueStatus.ACTIVE,
  });

  // Editable amounts in MAJOR units (dollars/euros/pounds), keyed by
  // contribution id. Only AGGREGATE boxes are tracked here; FORMULA totals are
  // read-only and render their live (refetched) value directly.
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Seed editable amounts from the fetched contributions without clobbering
  // in-flight edits (revalidation can re-run this effect). Reset when closed.
  useEffect(() => {
    if (!isOpen) {
      setAmounts((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    setAmounts((prev) => {
      let changed = false;
      const next = { ...prev };
      contributions.forEach((contribution) => {
        if (next[contribution.id] === undefined) {
          next[contribution.id] = contributionMajorString(contribution);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [isOpen, contributions]);

  const currencySymbol = IndirectTaxFilingsHelper.getCurrencySymbol(currency);

  const sections = useMemo<VatReportSection[]>(() => {
    const sorted = [...contributions].sort((a, b) => a.boxOrder - b.boxOrder);
    const isMultiJurisdiction = sorted.some(
      (c) => c.filingThroughJurisdiction != null
    );
    if (!isMultiJurisdiction) {
      return [{ title: 'Breakdown', rows: sorted }];
    }
    const byJurisdiction = new Map<string, IIndirectTaxFilingV2Contribution[]>();
    sorted.forEach((contribution) => {
      const key = contribution.filingThroughJurisdiction ?? 'Unspecified';
      const list = byJurisdiction.get(key) ?? [];
      list.push(contribution);
      byJurisdiction.set(key, list);
    });
    return Array.from(byJurisdiction.keys())
      .sort()
      .map((jurisdiction) => ({
        title: IndirectTaxRegionHelper.getCountryName(jurisdiction),
        rows: byJurisdiction.get(jurisdiction) ?? [],
      }));
  }, [contributions]);


  const persistContributions = async (): Promise<{
    ok: boolean;
    error?: string;
  }> => {
    const payload = contributions.map((contribution) => {
      const raw = amounts[contribution.id];
      const parsed = raw == null || raw.trim() === '' ? NaN : Number(raw);

      const adjustedValue = Number.isFinite(parsed)
        ? Math.round(parsed * 100)
        : null;
      return { id: contribution.id, adjustedValue };
    });

    const result = await updateIndirectTaxFilingV2Contributions({
      filingId,
      params: { corporationId, contributions: payload },
    });

    if (!result.success) {
      return { ok: false, error: result.error };
    }
    await mutateContributions();
    return { ok: true };
  };

  const handleSave = async () => {
    setIsSaving(true);
    const { ok, error } = await persistContributions();
    setIsSaving(false);

    if (ok) {
      setNotification({
        show: true,
        valid: true,
        title: 'Filing report saved',
        message: 'Your changes have been saved.',
      });
      onClose();
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to save filing report',
      });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const { ok, error } = await persistContributions();
    if (!ok) {
      setIsSubmitting(false);
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to submit filing report',
        message: error,
      });
      return;
    }

    // Complete the agent's GENERATE_FILING_REPORT task — the backend cascades
    // REVIEW_TRANSACTIONS to IN_PROGRESS (handing off to the client).
    await updateTask({
      id: taskId,
      data: { status: TaskStatus.COMPLETED },
    });

    // Resolve any open issue the client raised, so re-submitting clears it.
    const activeIssues = issuesData?.issues ?? [];
    await Promise.all(
      activeIssues.map((issue) =>
        updateServiceRequestIssue(issue.id, {
          status: ServiceRequestIssueStatus.RESOLVED,
        })
      )
    );

    await Promise.all([mutateContributions(), mutateIssues()]);
    setIsSubmitting(false);
    setNotification({
      show: true,
      valid: true,
      title: 'Filing report submitted',
      message: 'The filing report has been sent to the client for approval.',
    });
    onSubmitted();
    onClose();
  };

  const isBusy = isSaving || isSubmitting;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => (open ? undefined : onClose())}
    >
      <DialogContent className="flex flex-col gap-xl min-w-[800px] max-h-[85vh]">
        <DialogHeader className="border-b-0">
          <div>
            <h3 className="txt-lg-bold text-text-primary">Filing report</h3>
            <span className="txt-small-regular text-text-tertiary">
              Review the figures for each box and adjust the amounts where
              needed. Save to keep your progress, or Submit to hand the filing
              off to the client for approval.
            </span>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-3xl overflow-y-auto px-6">
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : !contributions ? (
            <div className="flex flex-col items-center justify-center gap-xs py-10 text-center">
              <p className="txt-small-medium text-text-primary">
                No filing data to review yet
              </p>
              <p className="txt-small-regular text-text-tertiary max-w-[420px]">
                Box values for this filing haven&apos;t been prepared yet. Once
                they&apos;re available you&apos;ll be able to review and adjust
                them here.
              </p>
            </div>
          ) : (
            sections.map((section) => (
              <div key={section.title} className="flex flex-col gap-md">
                <div className="flex items-center justify-between">
                  <span className="txt-extra-small-medium text-text-tertiary">
                    {section.title}
                  </span>
                  <span className="txt-extra-small-medium text-text-tertiary">
                    Amount ({currency})
                  </span>
                </div>
                <div className="border border-border-primary rounded-xl overflow-hidden">
                  {section.rows.map((contribution, index) => {
                    return (
                      <FilingInputRow
                        key={contribution.id}
                        title={boxLabel(contribution)}
                        // FORMULA (read-only) rows show the live, refetched
                        // value so recomputed totals reflect saved edits.
                        value={amounts[contribution.id] ?? ''}
                        onChange={(value) => {
                          setAmounts((prev) => ({
                            ...prev,
                            [contribution.id]: value,
                          }));
                        }}
                        placeholder="Enter amount"
                        prefix={currencySymbol}
                        type="number"
                        className={
                          index !== section.rows.length - 1 ? 'border-b' : ''
                        }
                      />
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="border-t">
          <div className="flex justify-end gap-md">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSave}
              disabled={isBusy || !contributions}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleSubmit}
              disabled={isBusy || !contributions}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
